import {
  differenceInHours,
  format,
  formatDistanceToNowStrict,
  intervalToDuration,
  subDays,
} from "date-fns";
import { enCA } from "date-fns/locale";
import type React from "react";

import Config from "../config";
import { AuthStorage } from "../local-storage";

export const onClickAccessible = (callback: () => void) => {
  return {
    onMouseDown: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      callback();
      e.preventDefault();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        callback();
      }
    },
  };
};

export const logout = () => {
  if (Config.isDev) {
    const authStorage = new AuthStorage();
    authStorage.clear();
  }
  window.location.href = "/logout";
};

export function assertIsError(error: unknown): asserts error is Error {
  if (!(error instanceof Error)) {
    throw error;
  }
}

export const normalizePathname = (pathname: string): string =>
  pathname.replace(/\/$/, "");

// Date formatter to format date consistently. We can use this to localize as well eventually.
// (param) timestamp:  an ISO 8601 formatted timestamp as 2025-05-14T19:55:41.585585Z
export const formatDateTimeString = (timestamp: string) => {
  const date = new Date(timestamp);
  const timeString = format(date, "p", { locale: enCA });
  const dateString = format(date, "P", { locale: enCA });

  return `${dateString}, ${timeString}`;
};

export const formatDistance = (timestamp: string) => {
  const fromNowString = formatDistanceToNowStrict(new Date(timestamp), {
    addSuffix: true,
  });
  return fromNowString;
};

export const formatTimestamp = (timestamp: string) => {
  if (differenceInHours(new Date(), new Date(timestamp)) < 24) {
    return formatDistance(timestamp);
  }
  return formatDateTimeString(timestamp);
};

// Helper function to get duration parts
const getDurationParts = (durationInMs: number) => {
  const startDate = new Date();
  const endDate = startDate.getTime() + durationInMs;

  // While we could pass in 0 for start, and the duration for end, this makes is slightly more clear what the function is doing
  const duration = intervalToDuration({
    start: startDate,
    end: endDate,
  });

  const parts = [];
  if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
  if (duration.minutes && duration.minutes > 0)
    parts.push(`${duration.minutes}m`);
  if (duration.seconds && duration.seconds > 0)
    parts.push(`${duration.seconds}s`);

  return parts;
};

// Duration formatter to format duration like 1d 2h 33m 45s, usually in reference to job duration
// This does not currently localize the duration, but it could be added in the future
export const formatDuration = (durationInMs: number) => {
  const parts = getDurationParts(durationInMs);
  return parts.join(" ");
};

// Same as formatDuration, but with a max character length and uses ">" to indicate truncation
export const formatShortDuration = (
  durationInMs: number,
  maxCharacters: number = 6,
) => {
  const parts = getDurationParts(durationInMs);
  let formattedText = parts.join(" ");

  if (formattedText.length > maxCharacters) {
    const truncatedParts = [...parts];
    while (
      truncatedParts.join(" ").length > maxCharacters - 1 &&
      truncatedParts.length > 1
    ) {
      truncatedParts.pop(); // Remove the last (smallest) time unit
    }
    formattedText = ">" + truncatedParts.join(" ");
  }

  return formattedText;
};
